import { useCallback, useMemo, useState } from 'react';
import { useField, useFormikContext } from 'formik';
import { EnterpriseDto, ReferentialKey } from 'api/referential';
import { useAppSelector } from 'app/hooks';
import { selectReferentialData } from 'features/referentials/selectors/selectReferentialDataByKey';
import { legalFormCodeFromNatureJuridique } from 'helpers/legalFormCodeFromNatureJuridique';
import { referentialsApi } from 'services/swagger-api';
import { AddressFormFieldNames } from 'shared/enums/addressFormFieldNames';
import { BaseLegalEntityFormFieldNames } from 'shared/enums/baseLegalEntityFormFieldNames';
import { NsKey } from 'enums/NsKey';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { AutoComplete } from '../AutoComplete';
import { AutoCompleteItem } from '../AutoCompleteTypes';
import { FormField } from '../FormField';

const MIN_QUERY_LENGTH = 3;
const MAX_SUGGESTIONS = 6;
const POSTAL_CODE_REGEX = /^\d{5}$/;

interface FormikCompanySearchProps {
  name: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
}

/** Item du dropdown : libellé « raison sociale — CP ville » + la fiche complète. */
interface CompanyItem extends AutoCompleteItem {
  company: EnterpriseDto;
}

/**
 * Champ nom d'entreprise adossé au composant AutoComplete maison, avec les suggestions
 * de l'annuaire des entreprises (proxy backend). La saisie reste libre (une entreprise
 * absente de l'annuaire se tape normalement) ; rien n'est rempli automatiquement :
 * seule une sélection EXPLICITE dans la liste remplit le SIRET, la raison sociale et
 * l'adresse du siège — sans écraser un champ déjà saisi, hormis le nom et le SIRET qui
 * sont la sélection elle-même. Un code postal déjà renseigné restreint la recherche.
 */
export const FormikCompanySearch: React.FC<FormikCompanySearchProps> = ({
  name,
  label,
  placeholder,
  required,
  disabled = false,
}) => {
  const { t } = useDynamicTranslation([NsKey.Common]);
  const { values, setFieldValue, setFieldTouched } = useFormikContext<Record<string, unknown>>();
  const [field, meta] = useField<string>(name);
  const [items, setItems] = useState<CompanyItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const referentialsData = useAppSelector(selectReferentialData);
  const legalFormCodes = useMemo(
    () => (referentialsData[ReferentialKey.LegalForm] ?? []).map((item) => item.code),
    [referentialsData]
  );

  // Source de vérité unique : l'affichage suit la valeur Formik en direct, y compris les
  // écritures programmatiques (préremplissage SIRET, valeurs chargées en asynchrone).
  // Mémoïsé sur field.value pour garder une référence stable (AutoComplete synchronise
  // son inputValue via un useEffect dépendant de la référence de `value`).
  const displayedItem = useMemo<AutoCompleteItem | null>(
    () => (field.value ? { id: 'current', label: field.value } : null),
    [field.value]
  );

  const rawPostalCode = values[AddressFormFieldNames.PostalCode];
  const postalCode =
    typeof rawPostalCode === 'string' && POSTAL_CODE_REGEX.test(rawPostalCode.trim())
      ? rawPostalCode.trim()
      : undefined;

  const handleSearch = useCallback(
    async (query: string) => {
      // La saisie libre suit la frappe : le nom reste soumis même sans sélection.
      setFieldValue(name, query);

      if (query.length < MIN_QUERY_LENGTH) {
        setItems([]);
        return;
      }

      setIsLoading(true);
      try {
        const results = await referentialsApi.searchEnterprises({
          query,
          codePostal: postalCode,
          maxResultCount: MAX_SUGGESTIONS,
        });
        setItems(
          results
            .filter((company) => company.siret && company.raisonSociale)
            .map((company) => ({
              id: company.siret!,
              label: [
                company.raisonSociale,
                [company.codePostal, company.commune].filter(Boolean).join(' '),
              ]
                .filter(Boolean)
                .join(' — '),
              company,
            }))
        );
      } catch {
        // Annuaire indisponible : la saisie libre continue sans suggestions.
        setItems([]);
      } finally {
        setIsLoading(false);
      }
    },
    [name, postalCode, setFieldValue]
  );

  const fillIfEmpty = useCallback(
    (fieldName: string, value?: string | null) => {
      if (!value) return;
      const current = values[fieldName];
      if (typeof current === 'string' && current.trim() !== '') return;
      setFieldValue(fieldName, value);
    },
    [values, setFieldValue]
  );

  const handleChange = useCallback(
    (item: AutoCompleteItem | null) => {
      if (!item) {
        setFieldValue(name, '');
        return;
      }

      const { company } = item as CompanyItem;

      // Le nom et le SIRET sont la sélection elle-même ; le reste ne comble que les vides.
      setFieldValue(name, company.raisonSociale);
      if (company.siret) {
        setFieldValue(BaseLegalEntityFormFieldNames.RegistrationNumber, company.siret);
      }
      // Legal form only from a directory category that maps to a referential code.
      fillIfEmpty(
        BaseLegalEntityFormFieldNames.LegalReason,
        legalFormCodeFromNatureJuridique(company.natureJuridiqueCode, legalFormCodes)
      );
      fillIfEmpty(AddressFormFieldNames.Address, company.adresseLigne1);
      fillIfEmpty(AddressFormFieldNames.PostalCode, company.codePostal);
      fillIfEmpty(AddressFormFieldNames.City, company.commune);
      fillIfEmpty(AddressFormFieldNames.Country, company.adresseLigne1 ? 'FR' : null);
    },
    [name, setFieldValue, fillIfEmpty, legalFormCodes]
  );

  // Label et erreur rendus par FormField, comme tous les autres champs Formik —
  // l'AutoComplete garde uniquement l'input et le dropdown (typographie homogène).
  return (
    <FormField
      label={label}
      required={required}
      name={name}
      error={meta.touched && meta.error ? t(meta.error, { defaultValue: meta.error }) : ''}
    >
      <AutoComplete
        placeholder={placeholder}
        disabled={disabled}
        value={displayedItem}
        onChange={handleChange}
        onSearch={handleSearch}
        items={items}
        isLoading={isLoading}
        noResultsText="Aucune entreprise trouvée dans l'annuaire"
        minChars={MIN_QUERY_LENGTH}
        // Commit au blur : onSearch ne remonte que les saisies >= minChars, donc un texte
        // court tapé sans sélection doit quand même devenir la valeur du champ.
        onBlur={(e) => {
          setFieldValue(name, e.target.value);
          setFieldTouched(name, true);
        }}
      />
    </FormField>
  );
};
